import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {BasicButton} from '../../../widgets/components/UI/BasicButton'
import {useAuth} from '../../../auth'
import {Modal, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import folderIcon from '../../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'
import {getLinkFilesApi, getRecordsFolder, getSourceReferenceData} from '../../core/_requests'
import {getFileType} from '../../../../core/_util'
import {useDateFormat} from '../../../../hooks/useDateFormat'
import {useTimeFormat} from '../../../../hooks/useTimeFormat'
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  ColumnFiltersState,
  FilterFn,
} from '@tanstack/react-table'
import {rankItem} from '@tanstack/match-sorter-utils'
import {flexRender} from '@tanstack/react-table'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'

interface FolderStructureTypes {
  id: string
  name: string
  fileVersionName: string
  type: string
  lastModified: string
  activities: number
}

export const LinkedRecordsModal = ({
  showLinkedRecordsModal,
  setShowLinkedRecordsModal,
  recordId,
  getAllLinkedRecords,
  selectedLinkedRecordsIds,
}: {
  showLinkedRecordsModal: boolean
  setShowLinkedRecordsModal: Dispatch<SetStateAction<boolean>>
  recordId: string | undefined
  getAllLinkedRecords: (recordId: string) => void
  selectedLinkedRecordsIds: string[]
}) => {
  const {formatMessage} = useIntl()
  const {companyId} = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [checkedOptions, setCheckedOptions] = useState<any>({})
  const selectedFolders = useRef<any[]>([])
  const [selectedPath, setSelectedPath] = useState<string[]>([])
  const [storageListIds, setStorageListIds] = useState<string[]>([])
  const {getDateValue} = useDateFormat()
  const {getTimeValue} = useTimeFormat()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const selectedFoldersData = useRef<any[]>([])
  const [isLinkLoading, setIsLinkLoading] = useState(false)
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([])

  async function fetchSourceStorage() {
    if (!companyId) return
    setIsLoading(true)
    try {
      const {
        data: {success, data, errors},
      } = await getSourceReferenceData('recordKeeperSourceStorageList', companyId)
      if (success) {
        setStorageListIds(data.map((item: any) => item.sourceId))
      } else {
        console.error(errors)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSourceStorage()
  }, [showLinkedRecordsModal]) // eslint-disable-line react-hooks/exhaustive-deps

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({itemRank})
    return itemRank.passed
  }

  const handleDoubleFolderClick = async (
    path: string[],
    folderContent?: any,
    selectedFolderNameFromPath?: string
  ) => {

    if (selectedFolderNameFromPath) {
      const selectedPathIndex = path.indexOf(selectedFolderNameFromPath)
      setSelectedPath(selectedPath.slice(0, selectedPathIndex + 1))
    } else {
      setSelectedPath(path.filter((pathValue) => pathValue.length > 0))
    }
    try {
      setIsLoading(true)

      const {
        data: {data, success, errors},
      } = await getRecordsFolder(
        companyId!,
        storageListIds,
        folderContent ? folderContent?.nextFolderLevel : 'LevelOne',
        folderContent ? folderContent?.id : null,
      )

      if (success) {

        selectedFolders.current = data.filter(
          (item: any) => item.type !== 'file' || (item.type === 'file' && item.id !== recordId)
        )
        selectedFolders.current = data.filter(
          (item: any) => !selectedLinkedRecordsIds.includes(item.id) && item.id !== recordId
        )
        selectedFoldersData.current = [
          ...selectedFoldersData.current,
          ...data.filter(
            (item: any) => item.type !== 'file' || (item.type === 'file' && item.id !== recordId)
          ),
        ]
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (storageListIds.length > 0 && showLinkedRecordsModal) {
      handleDoubleFolderClick([])
    }
  }, [storageListIds, selectedLinkedRecordsIds,showLinkedRecordsModal]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecordKeeperFilesSync = async () => {
    if (!companyId) return
    if (!recordId) return
    if (selectedRecordIds.length === 0) {
      toast.error('Please select a file to link')
      return
    }
    try {
      setIsLinkLoading(true)
      const {
        data: {success, errors},
      } = await getLinkFilesApi(recordId, companyId, selectedRecordIds)
      if (success) {
        setShowLinkedRecordsModal(false)
        toast.success('Files linked successfully')
        setSelectedRecordIds([])
        setCheckedOptions({})
        getAllLinkedRecords(recordId)
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsLinkLoading(false)
    }
  }

  const columns = React.useMemo<ColumnDef<FolderStructureTypes, any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const row = info.row.original
          return (
            <div className='ms-2 text-dark'>
              {row.type === 'folder' ? (
                <img src={folderIcon} alt={row.name} />
              ) : (
                <img src={getFileType(row.fileVersionName)} alt={row.name}  style={{ height: '30px', width: '30px' }} />
              )}

              <span className='ms-2'>{row.name}</span>
            </div>
          )
        },
        enableSorting: true,
      },
      {
        accessorKey: 'lastModified',
        id: 'lastModified',
        header: 'Last Modified',
        cell: (info) => {
          const row = info.row.original

          return (
            <div className={`px-3 text-dark`}>{`${getDateValue(row.lastModified)}, ${getTimeValue(
              row.lastModified
            )}`}</div>
          )
        },
        enableSorting: true,
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const table = useReactTable({
    data: selectedFolders.current,
    columns,
    state: {
      columnFilters,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    }, // Provide an empty object if no custom filters are used
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getSortedRowModel: getSortedRowModel(),
  })

  const handleCheckboxChange = (event: any, recordId: string) => {
    setCheckedOptions((prevState: any) => ({
      ...prevState,
      [recordId]: event.target.checked,
    }))
    if (event.target.checked) {
      setSelectedRecordIds((prevState) => Array.from(new Set([...prevState, recordId])))
    } else {
      setSelectedRecordIds((prevState) => prevState.filter((id) => id !== recordId))
    }
  }

  return (
    <Modal
      show={showLinkedRecordsModal}
      onHide={() => {
        setSelectedRecordIds([])
        setCheckedOptions({})

        setShowLinkedRecordsModal(false)
      }}
      aria-labelledby='contained-modal-title-vcenter'
      centered
      size='lg'
    >
      <Modal.Header closeButton>
        <h2>{formatMessage({id: 'Link Files'})}</h2>
      </Modal.Header>
      <Modal.Body>
        {selectedPath.length >= 1 && (
          <div className='d-flex my-3 font-size-10'>
            <div className='d-flex align-items-center'>
              <img src={folderIcon} width={15} height={15} alt='root' />
              <div
                className='ms-1 text-primary'
                role='button'
                onClick={() => {
                  setCheckedOptions({})
                  setSelectedRecordIds([])
                  selectedFoldersData.current = []
                  handleDoubleFolderClick([])
                }}
              >
                /
              </div>
              <span className='text-dark mx-2 font-size-14'>{'>'}</span>
            </div>
            {selectedPath.map((path: string, index: number) => (
              <div key={index} className='d-flex align-items-center'>
                <img src={folderIcon} width={15} height={15} alt={path} />
                {selectedPath.length === index + 1 ? (
                  <div className='ms-1'>{path}</div>
                ) : (
                  <div
                    className='ms-1 text-primary'
                    role='button'
                    onClick={() => {
                      setCheckedOptions({})
                      setSelectedRecordIds([])
                      selectedFoldersData.current = selectedFoldersData.current.filter(
                        (item: any) => item !== undefined
                      )
                      handleDoubleFolderClick(
                        selectedPath,
                        selectedFoldersData.current.find((item: any) => item.name === path),
                        path
                      )
                    }}
                  >
                    {path}
                  </div>
                )}

                {index !== selectedPath.length - 1 && (
                  <span className='text-dark mx-2 font-size-14'>{'>'}</span>
                )}
              </div>
            ))}
          </div>
        )}
        <div className='overflow-x-auto hide-scrollbar'>
          <Table className='w-100 '>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 w-100 table-row border-dark-mode'
                >
                  {selectedFolders.current.some((folder) => folder.type === 'file') && (
                    <th className='pb-6 pt-4 table-column-width'>
                      <input
                        type='checkbox'
                        className='form-check-input h-25px w-25px mt-1'
                        checked={table
                          .getRowModel()
                          .rows.every(
                            (row) => row.original.type !== 'file' || checkedOptions[row.original.id]
                          )}
                        onChange={(event) => {
                          const isChecked = event.target.checked
                          const newCheckedOptions = {...checkedOptions}
                          const newSelectedRecordIds = [...selectedRecordIds]

                          table.getRowModel().rows.forEach((row) => {
                            if (row.original.type === 'file') {
                              newCheckedOptions[row.original.id] = isChecked
                              if (isChecked) {
                                if (!newSelectedRecordIds.includes(row.original.id)) {
                                  newSelectedRecordIds.push(row.original.id)
                                }
                              } else {
                                const index = newSelectedRecordIds.indexOf(row.original.id)
                                if (index > -1) {
                                  newSelectedRecordIds.splice(index, 1)
                                }
                              }
                            }
                          })

                          setCheckedOptions(newCheckedOptions)
                          setSelectedRecordIds(newSelectedRecordIds)
                        }}
                      />
                    </th>
                  )}
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className=' pb-6 pt-4 table-column-width'
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                <>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={2} className='text-center py-5 pt-10'>
                        {formatMessage({id:"No records found"})}
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className='border-bottom data-table-link-color text-dark'
                        onClick={() => {
                          const isChecked = !checkedOptions[row.original.id]
                          handleCheckboxChange({target: {checked: isChecked}}, row.original.id)
                        }}
                      >
                        {row.getVisibleCells().map((cell, index) => (
                          <>
                            {row.original.type === 'file' && index === 0 && (
                              <td key={cell.id} className='pt-4 border-0 cursor-pointer'>
                                <input
                                  type='checkbox'
                                  className='form-check-input h-25px w-25px mt-1'
                                  checked={checkedOptions[row.original.id]}
                                  onChange={(event) => {
                                    event.stopPropagation()
                                    handleCheckboxChange(event, row.original.id)
                                  }}
                                />
                              </td>
                            )}

                            <td
                              key={cell.id}
                              className='pt-4 border-0 cursor-pointer'
                              onClick={(event) => {
                                event.stopPropagation()
                                if (cell.row.original.type === 'folder') {
                                  handleDoubleFolderClick(
                                    [...selectedPath, cell.row.original.name],
                                    cell.row.original
                                  )
                                } 
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          </>
                        ))}
                      </tr>
                    ))
                  )}
                </>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex  gap-3'>
          <BasicButton
            buttonText={formatMessage({id: 'Cancel'})}
            border='none'
            color='#F5F8FA'
            textColor='#5E6278'
            minWidth={90}
            height='44px'
            disabled={isLinkLoading}
            onClick={() => {
              setShowLinkedRecordsModal(false)
            }}
          />

          <button
            type='submit'
            className=' btn btn-primary w-100'
            disabled={selectedRecordIds.length === 0 || isLinkLoading}
            onClick={handleRecordKeeperFilesSync}
          >
            {isLinkLoading ? (
              <span className='indicator-label font-size-13 p-0'>
                {formatMessage({id: 'Please wait...'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            ) : (
              <span className='indicator-label font-size-13 p-0'>
                {formatMessage({id: 'Link'})}
              </span>
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
